// verificationsSearch — the free-text predicate for the Rental Verifications
// table, in a dependency-free module (no React) so it stays unit-testable.
//
// WHY THIS EXISTS. The "Past applications" view is 300+ rows deep and the only
// reason anyone opens it is to find ONE household — an operator looking for a
// resident's verification in order to re-send it. Without a search box that is a
// scroll-and-squint exercise; with one it is a keystroke.
//
// The haystack is built from what an operator actually remembers: a person, an
// address, and the state a thing is in. Every field is already on the row, so
// searching costs no read.

import type { VerificationGroupSummary } from './verificationsClient';

/** Verification-status labels, mirrored from the table's STATUS_META. Kept here
 *  so a status can be typed by the word the row SHOWS ("Bounced"), not only by
 *  its wire value ('bounced'). */
const RECORD_STATUS_WORDS: Record<string, string> = {
  created: 'Queued',
  skipped: 'Skipped',
  'whp-internal-review': 'WHP internal',
  'no-contact': 'No contact',
  sent: 'Sent',
  reminded: 'Reminded',
  responded: 'Responded',
  declined: 'Declined',
  'denied-tenancy': 'Denied tenancy',
  'related-shortcut': 'Related',
  bounced: 'Bounced',
  'no-response': 'No response',
};

/** AppFolio application-status labels, mirrored from the table's AF_STATUS_LABEL. */
const AF_STATUS_WORDS: Record<string, string> = {
  approved: 'Approved',
  in_screening: 'In screening',
  info_needed: 'Info needed',
  in_review: 'In review',
  decision_pending: 'Decision pending',
  new: 'New',
  denied: 'Denied',
  canceled: 'Canceled',
};

/** A group with no explicit `active` flag (legacy API) is treated as ACTIVE. */
function isActive(g: VerificationGroupSummary): boolean {
  return g.active !== false;
}

/**
 * The searchable text of one row.
 *
 * Deliberately includes the PRIOR addresses and the landlords asked about them:
 * a rental verification IS an address, and "the Cedar Street one" is how an
 * operator refers to it. The groupId is in there too, so a uuid pasted out of a
 * log or an email lands on its row.
 */
export function verificationHaystack(g: VerificationGroupSummary): string {
  const words: Array<string | null | undefined> = [
    g.groupId,
    ...g.applicants.flatMap((a) => [a.firstName, a.lastName, `${a.firstName} ${a.lastName}`]),
    g.property?.name,
    g.property?.address,
    g.unit?.name,
    g.unit?.address,
    g.appfolioStatus,
    g.appfolioStatus ? AF_STATUS_WORDS[g.appfolioStatus] : null,
    // The review state, in the words the row itself shows.
    g.needsAttention ? 'Needs review' : 'Clear',
    g.flagCleared ? 'Reviewed' : null,
    g.pendingState === 'pending-scan' ? 'Pending scan' : null,
    g.pendingState === 'no-history' ? 'No history' : null,
    isActive(g) ? 'Active' : 'Past',
  ];
  for (const r of g.records ?? []) {
    words.push(
      r.status,
      RECORD_STATUS_WORDS[r.status],
      r.verdict,
      r.claimed.address.line1,
      r.claimed.address.line2,
      r.claimed.address.city,
      r.claimed.address.state,
      r.claimed.address.postalCode,
      r.landlord.name,
      r.landlord.email,
      ...r.applicantNames,
    );
  }
  return words.filter(Boolean).join(' ').toLowerCase();
}

/** Split a raw query into the tokens the predicate ANDs together. */
export function searchTokens(query: string): string[] {
  return query.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/**
 * AND over whitespace-separated tokens, so "garcia denied" NARROWS rather than
 * widening — the behaviour every other search in this app has. An empty query
 * matches everything.
 */
export function matchesVerificationQuery(
  g: VerificationGroupSummary,
  tokens: string[],
): boolean {
  if (tokens.length === 0) return true;
  const hay = verificationHaystack(g);
  return tokens.every((t) => hay.includes(t));
}
